import React, { useEffect, useRef, useState } from 'react'
import NonModalWindow from '../NonModalWindow'
import SimpleComboBox from '../SimpleComboBox'
import { useController, useForm, useWatch, Controller } from 'react-hook-form'
import _ from 'lodash'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import useAppStore from '@/store/useAppStore'

import AmountInput from '../AmountInput'
import { Label } from '../ui/Label'
import { unformatNumeral } from 'cleave-zen'
import { formatDate } from 'date-fns'
import { Checkbox } from '../ui/checkbox'
import { Textarea } from '../ui/Textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import DatePicker from '../ui/DatePicker'
import { RadioGroup, RadioGroupItem } from '../ui/RadioGroup'
import { Button } from '../ui/button'
import { TextField } from '../ui/TextField'
import { Description } from '../ui/Description'
import Field from '../ui/Field'
import FieldStatusMessage from '../ui/FieldStatusMessage'
import { Switch } from '../ui/switch'

import useGetTransactionCategories from '@/services/api/transaction-category/useGetTransactionCategories'
import useGetMoneyPaths from '@/services/api/money-path/useGetMoneyPaths'
//import useGetTransactionRepeatOptions from '@/services/api/cash-flow/transaction/useGetTransactionRepeatOptions'
import useGetModelsList from '@/services/api/model/useGetModelsList'
import useGetPeriodByOffset from '@/services/api/cache/useGetPeriodByOffset'
import useGetTransactionById from '@/services/api/cache/useGetTransactionById'
import useGetVault from '@/services/api/vault/useGetVault'
import useGetTransactionCategory from '@/services/api/transaction-category/useGetTransactionCategory'
import useGetVaults from '@/services/api/vault/useGetVaults'

import useCreateTransaction from '@/services/api/cash-flow/transaction/useCreateTransaction'
import useCreateTransfer from '@/services/api/cash-flow/transaction/useCreateTransfer'
import useUpdateTransaction from '@/services/api/cash-flow/transaction/useUpdateTransaction'
import useUpdateTransfer from '@/services/api/cash-flow/transaction/useUpdateTransfer'
import useCreateTransactionSeries from '@/services/api/cash-flow/transaction-series/useCreateTransactionSeries'
import useUpdateTransactionSeries from '@/services/api/cash-flow/transaction-series/useUpdateTransactionSeries'
import useActualiseTransaction from '@/services/api/cash-flow/transaction/useActualiseTransaction'

import CreateModel from '../model/CreateModel'
import CustomTransactionRepeat from './CustomTransactionRepeat'

import { buildList } from '@/utils/cron.js'

import {
  ArrowsUpDownIcon,
  ChevronLeftIcon,
  ListBulletIcon,
  PlusCircleIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid'
import useOpenNonModalWindow from '@/hooks/useOpenNonModalWndow'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { ENTITY_TYPES } from '@/data/constants/constants'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import useCloseToOrigin from '@/hooks/useCloseToOrigin'
import CloseConfirmationModal from '../CloseConfirmationModal'

interface TransactionFormProps {
  activeTab: 'new' | 'list'
}

function getSchema({ selectedEntity, editTransaction }) {
  return yup
    .object()
    .noUnknown()
    .shape({
      formType: yup.string(), //.oneOf(['income', 'expenses', 'transfer']),
      asModel: yup.boolean().default(true),
      agreeToActivateModel: yup.boolean(),
      modelId: yup.number().when('asModel', {
        is: true,
        then: (schema) => schema.required('Please fill in this field'),
        otherwise: (schema) => schema.nullable(),
      }),
      amount: yup
        .string()
        .required()
        .test({
          name: 'not-zero',
          message: 'Amount must be greater than 0',
          test: (val) => {
            const amountNum = parseFloat(unformatNumeral(val))
            return amountNum > 0
          },
        }),
      description: yup.string().nullable(),
      category: yup.object({ id: yup.number() }).when((val, schema) => {
        if (
          [ENTITY_TYPES.TRANSACTION_CATEGORY, ENTITY_TYPES.VAULT].includes(
            selectedEntity?.entity_type,
          )
        ) {
          return schema.nullable()
        } else {
          schema.required()
        }
      }),
      vault: yup
        .object() //.required(),
        .when('formType', {
          is: 'transfer',
          then: (schema) => schema.required('This field is required.'),
          otherwise: (schema) => schema.nullable(),
          // is: (val) => {
          //   return val === 'transfer' && selectedEntity?.id
          // },
          // then: (schema) => schema.nullable(),
          // otherwise: (schema) => schema.required('This field is required.'),
        }),
      toVault: yup.object().when('formType', {
        is: 'transfer',
        then: (schema) => schema.required('This field is required.'),
        otherwise: (schema) => schema.nullable(),
      }),
      refund: yup.boolean().default(false),
      isSkipAllocations: yup.boolean().default(false),
      behaviour: yup.string().oneOf(['Standard', 'EstimateOnly']).default('Standard'),
      series: yup
        .object()
        .required()
        .test({
          name: 'not-custom',
          message: 'Invalid Selection',
          test: (val) => {
            return val?.name !== 'Custom'
          },
        }),
      postDate: yup.date().required(),
      // .test({
      //   name: 'within-range',
      //   message: 'Invalid date selection',
      //   test: (val, ctx) => {
      //     if (!period) return true

      //     return isWithinInterval(val, {
      //       start: startOfDay(new Date(period.start)),
      //       end: new Date(period.end),
      //     })
      //   },
      // }),
    })
    .when((value, schema) => {
      if (value[0].formType === 'transfer' && !editTransaction) {
        return schema.shape({
          category: yup.object().notRequired().nullable(),
          toVault: yup.object().required('This field is required.'),
          // .test({
          //   name: 'not-same-as-from-vault',
          //   message: 'From and To vault can not be same',
          //   test: (val, ctx) => {
          //     return val.id !== selectedEntity?.id
          //   },
          // }),
        })
      }
    })
  // .when((value, schema) => {
  //   if (value[0].formType === 'transfer' && !editTransaction) {
  //     return schema.shape({
  //       category: yup.object().notRequired().nullable(),
  //       toVault: yup
  //         .object()
  //         .required()
  //         .test({
  //           name: 'not-same-as-from-vault',
  //           message: 'From and To vault can not be same',
  //           test: (val, ctx) => {
  //             return val.id !== value[0]?.vault?.id
  //           },
  //         }),
  //     })
  //   }
  // })
}



export default function TransactionForm({
  activeTab = 'new',
  // ...props
}: TransactionFormProps) {
  const {
    categoryId,
    vaultId,
    transactionId,
    modelId: forModelId,
  } = useParams()

  const [searchParams] = useSearchParams()
  const type = searchParams.get('type') //in|out|transfer
  const offset = searchParams.get('offset')
  // const editSeries = searchParams.get('editSeries') === 'true'
  const [editSeries, setEditSeries] = useState(false)

  const navigate = useNavigate()

  const closeToOrigin = useCloseToOrigin()
  const location = useLocation()

  const {
    data: _categoryEntity,
    isLoading: isLoadingCategory,
    refetch: refetchTransactionCategory,
  } = useGetTransactionCategory({ id: categoryId })

  const { data: vaultEntity, isLoading: vaultEntityIsLoading } = useGetVault({
    vaultId,
  })

  const categoryEntity = !!vaultId ? vaultEntity : _categoryEntity

  const { data: moneyPaths } = useGetMoneyPaths()
  const formatter = useNumberFormatter({})

  const { data: editTransaction, refetch: refetchEditTransaction } =
    useGetTransactionById({ id: transactionId })

  const { data: period, refetch: refetchPeriod } = useGetPeriodByOffset({
    offset,
  })

  const { data: vaults } = useGetVaults()
  const {action: createTransactionSeries, status: createTransactionSeriesStatus} = useCreateTransactionSeries();
  const {action: updateTransactionSeries, status: updateTransactionSeriesStatus} = useUpdateTransactionSeries();
  const { action: createTransaction, status: createTransactionStatus } =
    useCreateTransaction()

  const { data: models } = useGetModelsList()

  const { action: updateTransaction, status: updateTransactionStatus } =
    useUpdateTransaction({ id: editTransaction?.id })

  const { action: updateTransfer, status: updateTransferStatus } =
    useUpdateTransfer({ id: editTransaction?.id })

  const { action: createTransfer } = useCreateTransfer()

  const { action: actualise, status: actualiseStatus } = useActualiseTransaction({ id: editTransaction?.id })

  const categoryApi =
    type === 'in' ? 'income' : type === 'out' ? 'expenses' : undefined

  const { data: categories, status } = useGetTransactionCategories({
    type: categoryApi,
  })

  const disableSeriesSelection = editTransaction?.transaction_series_id && !editSeries

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [behaviourAutoReset, setBehaviourAutoReset] = useState(false)

  const [shortcuts, setShortcuts] = useState([])

  // const { updateExistingWindow } = useOpenNonModalWindow()
  const config = {
    in: {
      title: 'Add Money In Transaction',
    },
    out: {
      title: 'Add Money Out Transaction',
    },
    transfer: {
      title: 'Add Transfer',
    },
  }

  const title = config[type]?.title

  // const setNonModalWindowState = useAppStore(
  //   (state) => state.setNonModalWindowState,
  // )

  const [openCustomTransactionReport, setOpenCustomTransactionReport] =
    useState(false)
  const [openCreateModel, setOpenCreateModel] = useState(false)

  // Tracks the series selection before the custom modal opens so we can
  // restore it if the user closes the modal without submitting.
  const previousSeriesRef = useRef(null)
  // Prevents the shortcuts useEffect from overwriting the series value that
  // submitCustomSeries already set explicitly.
  const customSubmittedRef = useRef(false)

  const vaultsFlat = _.flatten(
    vaults?.map((vault) => {
      const res = vault.is_group ? vault.vaults : [vault]
      return res.filter((x) => !x.is_archived)
    }),
  )

  const categoriesFlat = _.flatten(
    categories?.map((category) => {
      return category.is_group ? category.transaction_categories : [category]
    }),
  )
  const showCategoryField = type !== 'transfer'

  const {
    register,
    handleSubmit,
    formState: { errors, defaultValues, isDirty },
    reset,
    control,
    setError,
    setValue,
  } = useForm({
    defaultValues: async () => {
      let editTransaction = null
      if (transactionId) {
        const { data: _editTransaction } = await refetchEditTransaction()
        editTransaction = _editTransaction
      }

      let _categoryEntity = categoryEntity
      if (categoryId) {
        const { data: _category } = await refetchTransactionCategory({
          cancelRefetch: false,
        })
        _categoryEntity = _category
      }

      let period = null
      if (offset) {
        const { data: _period } = await refetchPeriod({ cancelRefetch: false })
        period = _period
      }

      // const { data: repeatOptions } = await refetchRepeatOptions({
      //   cancelRefetch: false,
      // })

      function defaultVault() {
        if (editTransaction) {
          if (type === 'transfer') {
            return vaultsFlat.find(
              (x) => x.id === editTransaction?.from.entity_id,
              // ||  editTransaction?.vault_id,
            )
          } else {
            return vaultsFlat.find((x) => x.id === editTransaction?.vault_id)
          }
        }

        if (vaultId) {
          return vaultsFlat.find((x) => x.id === Number(vaultId))
        }

        if (_categoryEntity?.default_vault_id) {
          return vaultsFlat.find(
            (x) => x.id === _categoryEntity?.default_vault_id,
          )
        }

        if (type === 'in') {
          return vaultsFlat.find((x) => !!x.is_default_for_income)
        } else if (type === 'out') {
          return vaultsFlat.find((x) => !!x.is_default_for_expense)
        }
        return null
      }

      const _shortcuts = buildList(
        editTransaction?.transaction_date ?? formatDate(new Date(), 'yyyy-MM-dd'),
        editTransaction?.transaction_series,
      )
      setShortcuts(_shortcuts)

      // Determine which shortcut to pre-select from the existing series data.
      let defaultSeries = _shortcuts.find(x => x.id === -1) // "Does not repeat"
      if (editTransaction?.transaction_series) {
        const ts = editTransaction.transaction_series
        const standardMatch = _shortcuts.find(x =>
          !x.is_hidden && !x.is_open_settings && x.id !== -99999 &&
          x.cron === ts.cron && x.type === ts.type && x.frequency === ts.frequency &&
          (x.ends_after_count ?? undefined) === (ts.ends_after_count ?? undefined) &&
          (x.ends_after_date ?? undefined) === (ts.ends_after_date ?? undefined)
        )
        if (standardMatch) {
          defaultSeries = standardMatch
        } else {
          // Custom pattern – use the -99999 entry that buildList created with a description
          defaultSeries =
            _shortcuts.find(x => x.id === -99999 && !x.is_hidden) ??
            _shortcuts.find(x => x.id === -1)
        }
      }

      // Prefer the real amount; fall back to the estimate for a zeroed
      // EstimateOnly forecast; never let a bad value become the string "NaN".
      const rawAmount = Number(
        editTransaction?.amount || editTransaction?.estimated_amount || 0,
      )
      const initialAmount = Number.isFinite(rawAmount) ? Math.abs(rawAmount) : 0

      return {
        asModel: editTransaction?.what_if_id > 0,
        modelId: forModelId ?? editTransaction?.what_if_id ?? null,
        agreeToActivateModel: false,
        amount: initialAmount.toString(),
        description: editTransaction?.description ?? '',
        category: editTransaction
          ? categoriesFlat.find(
              (x) => x.id === editTransaction.transaction_category_id,
            )
          : categoryId && _categoryEntity
            ? categoriesFlat.find((x) => x.id === _categoryEntity.id) ?? null
            : null,
        vault: defaultVault(),
        toVault:
          vaultsFlat.find((x) => x.id === editTransaction?.to.entity_id) ?? null,
        refund: type !== 'transfer' && (editTransaction?.is_refund ?? false),
        postDate: editTransaction
          ? new Date(editTransaction?.transaction_date)
          : period?.start
            ? new Date(period?.start)
            : new Date(),
        formType: type,
        isSkipAllocations: editTransaction?.is_skip_allocation ?? false,
        behaviour: type !== 'transfer'
          ? (editTransaction?.behaviour ?? 'Standard')
          : 'Standard',
        series: defaultSeries

      }
    },
    resolver: yupResolver(
      getSchema({
        showCategoryField,
        period,
        selectedEntity: categoryEntity,
        editTransaction: editTransaction,
      }),
    ),
  })

  const toVault = useWatch({
    control,
    name: 'toVault',
  })

  const postDate = useWatch({
    control,
    name: 'postDate',
  })
  // const {
  //   data: repeatOptions,
  //   status: repeatOptionsStatus,
  //   refetch: refetchRepeatOptions,
  // } = useGetTransactionRepeatOptions({
  //   date: editTransaction
  //     ? formatDate(new Date(editTransaction?.transaction_date), 'yyyy-MM-dd')
  //     : postDate
  //       ? formatDate(postDate, 'yyyy-MM-dd')
  //       : period?.start
  //         ? formatDate(new Date(period.start), 'yyyy-MM-dd')
  //         : formatDate(new Date(), 'yyyy-MM-dd'),
  //
  //   recurring_setting_id: editTransaction
  //     ? editTransaction?.recurring_setting_id
  //     : undefined,
  //
  //   enabled: !!postDate,
  // })

  // useEffect(() => {
  //   if (repeatOptionsStatus === 'success') {
  //     const selectedOption = repeatOptions?.find((x) => x.selected)
  //     setTimeout(() => {
  //       setValue('series', selectedOption)
  //     }, 0)
  //   }
  // }, [repeatOptionsStatus, repeatOptions])


  const { field: vaultField } = useController({
    control: control,
    name: 'vault',
    shouldUnregister: true,
  })

  const { field: toVaultField } = useController({
    control: control,
    name: 'toVault',
    shouldUnregister: true,
  })

  const { field: amountField } = useController({
    control: control,
    name: 'amount',
    // No shouldUnregister: the amount field is always present. Unregistering it
    // during the first-open loading race (skeleton -> form mount, Tabs `key`
    // flip when categoryEntity resolves) dropped its async-resolved default and
    // left the input rendering NaN.
  })

  const { field: seriesField } = useController({
    control: control,
    name: 'series',
    shouldUnregister: true,
  })

  const { field: modelIdField } = useController({
    control: control,
    name: 'modelId',
    shouldUnregister: true,
  })

  const { field: asModelField } = useController({
    control: control,
    name: 'asModel',
    shouldUnregister: true,
  })

  const { field: agreeToActivateModelField } = useController({
    control: control,
    name: 'agreeToActivateModel',
    shouldUnregister: true,
  })

  const { field: dateField } = useController({
    control: control,
    name: 'postDate',
    shouldUnregister: true,
  })

  const { field: refundField } = useController({
    control: control,
    name: 'refund',
    shouldUnregister: true,
  })

  const { field: isSkipAllocationsField } = useController({
    control: control,
    name: 'isSkipAllocations',
    shouldUnregister: true,
  })

  const { field: behaviourField } = useController({
    control: control,
    name: 'behaviour',
    shouldUnregister: true,
  })

  const category = useWatch({
    control,
    name: 'category',
  })


  // const formType = useWatch({
  //   control,
  //   name: 'formType',
  //   defaultValue: type,
  // })

  const vault = useWatch({
    control,
    name: 'vault',
  })

  const modelId = useWatch({
    control,
    name: 'modelId',
  })

  const asModel = useWatch({
    control,
    name: 'asModel',
  })

  const transactionDate = useWatch({
    control,
    name: 'postDate'
  })

  const isPastOrToday = (() => {
    if (!transactionDate) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const d = new Date(transactionDate)
    d.setHours(0, 0, 0, 0)
    return d <= today
  })()

  const isPastOrTodayEdit = !!editTransaction && !editSeries && isPastOrToday
  const isSeriesPastEdit = !!editTransaction && editSeries && isPastOrToday

  const selectedModel = asModel ? models?.find((x) => x.id === modelId) : null

  const toVaultOptions = vaultsFlat
    // .filter((x) => x.id !== categoryEntity?.id)
    .filter((x) => (type === 'transfer' ? x.id !== vault?.id : true))

  useEffect(() => {
    setValue('formType', type, { shouldValidate: true })
  }, [reset, type])

  function handleRefundChange(checked: boolean) {
    refundField.onChange(checked)
  }

  function handleIsSkipAllocationsChange(checked: boolean) {
    isSkipAllocationsField.onChange(checked)
  }

  function handleCategoryChange(category) {
    // Retain the vault if the form was opened for a specific vault, or if
    // the user already has a vault selected — changing category shouldn't
    // silently swap the vault out from under them.
    if (vaultId || vaultField.value) return

    const defaultVault = vaultsFlat.find(
      (vault: { id: number }) => vault.id === category?.default_vault_id,
    )
    setValue('vault', defaultVault ?? null, {
      shouldValidate: false,
    })
  }

  function handleSwapVaults() {
    const currentVault = vaultField.value
    const currentToVault = toVaultField.value
    setValue('vault', currentToVault ?? null)
    setValue('toVault', currentVault ?? null)
  }

  const handleClose = () => {
    if (location.key) {
      navigate(-1)
    } else {
      closeToOrigin()
    }
  }

  async function onSubmit(data) {
    const {
      amount,
      category: _cat,
      vault,
      postDate,
      series,
      description,
      toVault,
      modelId,
      agreeToActivateModel,
      refund,
      isSkipAllocations,
      behaviour,
    } = data
    console.log(data)
    const category = _cat ?? _categoryEntity
    const amountNum = parseFloat(unformatNumeral(amount))
    const date = formatDate(new Date(postDate), 'yyyy-MM-dd')

    if (
      asModel &&
      !forModelId &&
      modelId &&
      !selectedModel?.is_enabled &&
      !agreeToActivateModel
    ) {
      setError('modelId', { message: 'Please agree to activate the model.' })
      return
    }

    const payload = {
      transactionId: undefined,
      modelId: asModel ? modelId : undefined,
      amount: amountNum,
      is_refund: refund,
      categoryId: category?.id ?? undefined,
      vaultId: vault?.id,
      fromVaultId: undefined,
      date,
      description,
      is_skip_allocation: isSkipAllocations,
      behaviour: type !== 'transfer' ? (behaviour ?? 'Standard') : undefined,
      type: series?.type ?? undefined,
      frequency: series?.frequency ?? undefined,
      cron: series?.cron ?? undefined,
      ends_after_count: series?.ends_after_count ?? undefined,
      ends_after_date: series?.ends_after_date ?? undefined,
    }

    if (series?.cron && editTransaction?.id && !editSeries) {
      series.cron = undefined
    }

    if (series?.cron){
      if (type === 'transfer') {
        payload.vaultId = toVault?.id
        payload.fromVaultId = vault?.id
      }
      
      if (!editTransaction?.id) {
        await createTransactionSeries(payload)
        reset()
      } else {
        await updateTransactionSeries({
          // Always use the backend series ID, not the local shortcut placeholder.
          id: editTransaction?.transaction_series_id ?? series.id,
          transaction_id: editTransaction?.id,
          ...payload
        })
        // Close before cache invalidation refetches — the original transaction
        // is re-created with a new ID after a series update, so the current
        // edit URL is stale and would 404.
        handleClose()
      }
    }else{
      if (type === 'transfer') {
        if (editTransaction) {
          await updateTransfer({
            ...payload,
            vaultFrom: vault?.id,
            vaultTo: toVault?.id,
          })

          if (location.pathname.includes('transaction-list')) {
            const base = vaultId
                ? '/transaction-list/vaults'
                : '/transaction-list/categories'
            navigate({
              pathname: `${base}/${vaultId ? vaultId : categoryId}/transactions`,
              search: searchParams.toString(),
            })
          }
          handleClose()
        } else {
          await createTransfer({
            ...payload,
            vaultId: vault?.id,
            vaultToId: toVault.id,
          })
          reset()
        }
      } else {
        if (editTransaction) {
          await updateTransaction(payload)

          if (location.pathname.includes('transaction-list')) {
            const base = vaultId
                ? '/transaction-list/vaults'
                : '/transaction-list/categories'
            navigate({
              pathname: `${base}/${vaultId ? vaultId : categoryId}/transactions`,
              search: searchParams.toString(),
            })
          }
          handleClose()
          // closeToOrigin()
        } else {
          await createTransaction(payload)
          reset()
        }
      }

    }

  }


  const renderAllocations = () => {
    const base = parseFloat(unformatNumeral(amountField.value ?? ''))
    if (!Number.isFinite(base) || base === 0 || !moneyPaths) return <></>
    let total = 0
    return (
      <>
        {(() => {
          const arr = []
          _.forEach(moneyPaths, (i) => {
            const percentage = i?.current_path_date?.percentage ?? 0
            const allocation = Math.round(base * percentage) / 100
            total += allocation
            arr.push(
              <div className="flex justify-between">
                <div>{i.vault.name}</div>
                <div>{formatter(allocation)}</div>
              </div>,
            )
          })
          return arr
        })()}
        <div className="flex justify-between pt-2 font-bold">
          <div>Total:</div>
          <div>{formatter(total)}</div>
        </div>
        <div className="flex justify-between font-bold">
          <div>Left:</div>
          <div>{formatter(base - total)}</div>
        </div>
      </>
    )
  }

  function navigateTo(val: 'new' | 'list', close = false) {
    if (val === 'new') return

    const p = new URLSearchParams(searchParams)

    navigate(
      {
        pathname: `/transaction-list/${vaultId ? `vaults/${vaultId}` : categoryId ? `categories/${categoryId}` : 'undefined'}/transactions${close ? '' : '/new'}`,
        search: p.toString(),
      },
      { replace: true },
    )
  }


  const submitCustomSeries = (type, frequency, cron, ends_after_count, ends_after_date) => {
    const item = {
      // Retain the backend series ID so updateTransactionSeries targets the right record.
      id: editTransaction?.transaction_series_id ?? -99999,
      type,
      frequency,
      cron,
      ends_after_count,
      ends_after_date,
    }
    const newShortcuts = buildList(postDate, item)
    // Signal useEffect to skip – we'll set the value ourselves below.
    customSubmittedRef.current = true
    setShortcuts(newShortcuts)
    previousSeriesRef.current = null

    const customEntry = newShortcuts.find(x => x.id === -99999)
    if (customEntry?.is_hidden) {
      // The custom pattern matched a standard shortcut – find and select it.
      const standardMatch = newShortcuts.find(x =>
        !x.is_hidden && !x.is_open_settings && x.id !== -99999 &&
        x.cron === cron && x.type === type && x.frequency === frequency &&
        (x.ends_after_count ?? undefined) === (ends_after_count ?? undefined) &&
        (x.ends_after_date ?? undefined) === (ends_after_date ?? undefined),
      )
      setValue('series', standardMatch, { shouldValidate: true })
    } else {
      // Genuine custom pattern – select the descriptive entry.
      setValue('series', customEntry, { shouldValidate: true })
    }
  }

  useEffect(() => {
    // submitCustomSeries already set the value – nothing to do here.
    if (customSubmittedRef.current) {
      customSubmittedRef.current = false
      return
    }

    if (!seriesField.value || seriesField.value?.is_open_settings) {
      // Initial load (series not yet set) or date-change while custom modal is open.
      if (editTransaction?.transaction_series_id) {
        const ts = editTransaction.transaction_series
        const standardMatch = shortcuts.find(x =>
          !x.is_hidden && !x.is_open_settings && x.id !== -99999 &&
          x.cron === ts.cron && x.type === ts.type && x.frequency === ts.frequency &&
          (x.ends_after_count ?? undefined) === (ts.ends_after_count ?? undefined) &&
          (x.ends_after_date ?? undefined) === (ts.ends_after_date ?? undefined),
        )
        if (standardMatch) {
          setValue('series', standardMatch)
        } else {
          const customEntry = shortcuts.find(x => x.id === -99999 && !x.is_hidden)
          setValue('series', customEntry ?? shortcuts.find(x => x.id === -1))
        }
      } else {
        setValue('series', shortcuts.find(x => x.id === -1))
      }
    } else if (seriesField.value?.id) {
      // Shortcuts rebuilt (e.g. date changed) – refresh the selected entry so
      // its title reflects the new date (e.g. "Weekly on Tue" → "Weekly on Wed").
      const refreshed = shortcuts.find(x => x.id === seriesField.value?.id)
      setValue('series', refreshed ?? seriesField.value, { shouldValidate: true })
    }
  }, [shortcuts])

  useEffect(() => {
    setShortcuts(buildList(postDate, seriesField.value ?? editTransaction?.transaction_series))
  }, [postDate]);

  // Auto-reset behaviour to Standard when a past/today date is selected.
  useEffect(() => {
    if (type === 'transfer' || !transactionDate) return
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const isPast = new Date(transactionDate) <= today
    if (isPast) {
      if (behaviourField.value === 'EstimateOnly') {
        setValue('behaviour', 'Standard')
      }
      setBehaviourAutoReset(true)
    } else {
      setBehaviourAutoReset(false)
    }
  }, [transactionDate])

  if (!defaultValues || !postDate) return null

  const isLoading = vaultEntityIsLoading || isLoadingCategory

  return (
    <>
      <CreateModel
        open={openCreateModel}
        setOpen={setOpenCreateModel}
        onSuccess={(response) => {
          setValue('modelId', response?.id, { shouldValidate: true })
        }}
      />

      {openCustomTransactionReport && <CustomTransactionRepeat
        key={postDate.toString()}
        open={openCustomTransactionReport}
        onOpenChange={(val) => {
          if (!val && previousSeriesRef.current !== null) {
            // Modal closed without submitting – restore the previous selection.
            setValue('series', previousSeriesRef.current, { shouldValidate: true })
            previousSeriesRef.current = null
          }
          setOpenCustomTransactionReport(val)
        }}
        transactionDate={postDate}
        series={seriesField.value}
        submit={submitCustomSeries}
      />}
      <CloseConfirmationModal
        open={showConfirmationModal}
        setOpen={setShowConfirmationModal}
        onConfirm={() => {
          setShowConfirmationModal(false)

          if (location.pathname.includes('transaction-list')) {
            navigateTo('list', true)
            // navigate(-1)
          } else {
            closeToOrigin()
          }
        }}
      />

      <NonModalWindow
        isDirty={isDirty}
        isLoading={isLoading}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        title={categoryEntity?.name || forModelId ? undefined : title}
        onClose={() => {
          setShowConfirmationModal(isDirty)
          // setNonModalWindowState('closed')
        }}
      >
        {(categoryEntity || forModelId) && (
          <NonModalWindow.Header className="border-none">
            <div className="flex flex-1 gap-2">
              {(editTransaction || forModelId) && (
                <div>
                  <Button
                    type="button"
                    iconOnly
                    variant="ghost"
                    onClick={
                      () => navigate(-1)
                      // updateExistingWindow({
                      //   editTransaction: undefined,
                      //   activeTab: 'list',
                      //   rerender: true,
                      // })
                    }
                    size="sm"
                    className="size-7 self-start"
                  >
                    <ChevronLeftIcon className="size-4" />
                  </Button>
                </div>
              )}
              <div>
                <p>
                  {editTransaction?.id ? 'Edit: ' : null}{' '}
                  {categoryEntity?.name ?? title}
                </p>
                {forModelId && <Description>Model: {forModelId}</Description>}
                {period && (
                  <div className="mt-1 flex w-fit gap-2">
                    <div className="flex items-center justify-center rounded-full bg-gray-tr-12  px-1.5 py-[0.125rem] text-xs text-txt-primary group-data-[state=thisweek]:bg-accent-tr-24 group-data-[state=thisweek]:text-txt-accent">
                      <span>{period?.number}</span>
                    </div>
                    <div className="flex gap-2 text-sm font-normal text-txt-secondary">
                      <span>{formatDate(new Date(period.start), 'd MMM')}</span>
                      <span>—</span>
                      <span>
                        {formatDate(new Date(period.end), 'd MMM yyyy')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Button
              type="button"
              iconOnly
              variant="ghost"
              // onClick={() => closeToOrigin()}
              onClick={() => {
                setShowConfirmationModal(isDirty)
                if (!isDirty) {
                  if (location.pathname.includes('transaction-list')) {
                    // navigate(-1)
                    navigateTo('list', true)
                  } else {
                    closeToOrigin()
                  }
                }
              }}
              size="sm"
              className="size-7 self-start"
            >
              <XMarkIcon />
            </Button>
          </NonModalWindow.Header>
        )}
        <NonModalWindow.Body className="pt-2">
          <Tabs
            key={categoryEntity?.id ? 1 : 0}
            value={activeTab}
            className=""
            onValueChange={navigateTo}
          >
            {categoryEntity && !editTransaction && (
              <TabsList className="mb-4">
                <TabsTrigger value="new" className="flex gap-1">
                  <PlusCircleIcon /> <span>New</span>
                </TabsTrigger>
                <TabsTrigger value="list" className="flex gap-1">
                  <ListBulletIcon /> <span>List</span>
                </TabsTrigger>
              </TabsList>
            )}
            {/* <TabsContent value="list" className="">
              <TransactionList />
            </TabsContent> */}
            <TabsContent value="new" className="">
              <div className="gap-4- flex flex-col gap-field-gap">
                {!forModelId && (
                  <div className="flex flex-col gap-field-gap rounded-md bg-gray-low p-3">
                    <div className="flex items-center justify-between ">
                      <Label>Add as Model</Label>
                      <Switch
                        checked={asModelField.value}
                        onCheckedChange={asModelField.onChange}
                      />
                    </div>
                    {asModel && (
                      <div className="grid gap-field-gap">
                        <div>
                          <Field
                            status={
                              errors.modelId?.message ? 'error' : undefined
                            }
                            statusText={errors.modelId?.message.toString()}
                          >
                            <SimpleComboBox
                              align="end"
                              items={models}
                              selected={selectedModel}
                              setSelected={(item) => {
                                agreeToActivateModelField.onChange(false)
                                modelIdField.onChange(item.id)
                              }}
                              getOptionLabel={(item) => (
                                <div>
                                  <p>{item.name}</p>
                                </div>
                              )}
                              getSelectedLabel={(item) => item.name}
                              getValue={(item) => item.name}
                              fixedOption={
                                <div onClick={() => setOpenCreateModel(true)}>
                                  Create New Model
                                </div>
                              }
                            />
                            <FieldStatusMessage />
                          </Field>
                        </div>
                        {selectedModel && !selectedModel?.is_enabled && (
                          <div className="flex items-start gap-2">
                            <Checkbox
                              id="refund"
                              checked={agreeToActivateModelField.value}
                              onCheckedChange={
                                agreeToActivateModelField.onChange
                              }
                            />
                            <Label htmlFor="refund" className="font-normal">
                              <Description className="m-0">
                                Enable the model after adding the transaction
                              </Description>
                            </Label>
                            <div>
                              <Button
                                type="button"
                                iconOnly
                                className="size-4"
                                variant="ghost"
                                title="This is title"
                              >
                                <QuestionMarkCircleIcon />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {showCategoryField && (
                  <div>
                    <Field
                      status={errors.category?.message ? 'error' : undefined}
                      statusText={errors.category?.message.toString()}
                    >
                      <Label>Category</Label>
                      <Controller
                        shouldUnregister={true}
                        name="category"
                        control={control}
                        render={({ field }) => (
                          <SimpleComboBox
                            placeholder={`Select a ${type === 'in' ? 'Money In' : 'Money Out'} Category`}
                            items={categoriesFlat}
                            selected={field.value}
                            setSelected={(val) => {
                              field.onChange(val)
                              handleCategoryChange(val)
                            }}
                            getOptionLabel={(item) => (
                              <div>
                                <p>{item?.name}</p>
                                <Description>{item?.description}</Description>
                              </div>
                            )}
                            getSelectedLabel={(item) => item?.name}
                            getValue={(item) => item?.id}
                            clearable
                          />
                        )}
                      />
                      <FieldStatusMessage />
                    </Field>
                  </div>
                )}
                {/* If transfer and triggered from the cell Do nothing (categoryEntity is vault or category based on the cell the user has clicked) */}
                <div>
                  <Field
                    status={errors.vault?.message ? 'error' : undefined}
                    statusText={errors.vault?.message.toString()}
                  >
                    <Label>{type === 'in' ? 'To Vault' : 'From Vault'}</Label>
                    <SimpleComboBox
                      disabled={
                        !categoryEntity && type !== 'transfer' && !category
                      }
                      items={vaultsFlat.filter((x) => x.id !== toVault?.id)}
                      selected={vaultField.value}
                      setSelected={vaultField.onChange}
                      getOptionLabel={(item) => (
                        <div>
                          <p>{item.name}</p>
                          <Description>{item.description}</Description>
                        </div>
                      )}
                      getSelectedLabel={(item) => item.name}
                      getValue={(item) => item.id}
                      clearable
                    />
                    <FieldStatusMessage />
                  </Field>
                </div>
                {type === 'transfer' && (
                  <>
                    <div className="flex justify-end -my-1">
                      <Button
                        type="button"
                        iconOnly
                        variant="ghost"
                        size="sm"
                        onClick={handleSwapVaults}
                        title="Swap vaults"
                      >
                        <ArrowsUpDownIcon className="size-4" />
                      </Button>
                    </div>
                    <div>
                      <TextField
                        status={errors.toVault?.message ? 'error' : undefined}
                        statusText={errors.toVault?.message.toString()}
                      >
                        <TextField.Label>To Vault</TextField.Label>
                        <SimpleComboBox
                          items={toVaultOptions}
                          selected={toVaultField.value}
                          setSelected={toVaultField.onChange}
                          getOptionLabel={(item) => (
                            <div>
                              <p>{item.name}</p>
                              <Description>{item.description}</Description>
                            </div>
                          )}
                          getSelectedLabel={(item) => item.name}
                          getValue={(item) => item.id}
                          clearable
                        />
                      </TextField>
                    </div>
                  </>
                )}
                <div>
                  <Field
                    status={errors.postDate?.message ? 'error' : undefined}
                    statusText={errors.postDate?.message.toString()}
                  >
                    <Label>Date</Label>
                    <DatePicker
                      value={dateField.value}
                      selected={new Date(dateField.value)}
                      onSelect={(val) => dateField.onChange(new Date(val))}
                      excludeDisabled
                    ></DatePicker>
                    <FieldStatusMessage />
                  </Field>
                </div>

                {editTransaction && editTransaction?.transaction_series_id && (
                  <div>
                    <Label className="mb-2">Edit</Label>
                    <RadioGroup
                      className="flex flex-wrap gap-x-6 gap-y-2"
                      value={editSeries ? 'series' : 'thisOnly'}
                      onValueChange={(val) => {
                        setEditSeries(val === 'series')
                      }}
                    >
                      <Field className="flex-none">
                        <RadioGroupItem value="thisOnly" />
                        <Label>This Only</Label>
                      </Field>

                      <Field className="flex-none">
                        <RadioGroupItem value="series" />
                        <Label>This and Following</Label>
                      </Field>
                    </RadioGroup>
                  </div>
                )}
                {!disableSeriesSelection && (
                  <div>
                    <TextField
                        status={errors.series?.message ? 'error' : undefined}
                        statusText={errors.series?.message.toString()}
                    >
                      <SimpleComboBox
                          searchable={false}
                          items={shortcuts.filter(x => !x.is_hidden)}
                          selected={seriesField.value}
                          setSelected={(item) => {
                            if (item.is_open_settings) {
                              previousSeriesRef.current = seriesField.value
                              setValue('series', { ...seriesField.value, is_open_settings: true }, {
                                shouldValidate: false,
                              })
                              setOpenCustomTransactionReport(true)
                            } else {
                              setValue('series', item, { shouldValidate: true })
                            }
                          }}
                          getOptionLabel={(item) => (
                              <div>
                                <p>{item.title}</p>
                              </div>
                          )}
                          getSelectedLabel={(item) => item.title}
                          getValue={(item) => item.title}
                      />
                    </TextField>
                  </div>
                )}
                <div>
                  <Field
                    status={errors.amount?.message ? 'error' : undefined}
                    statusText={errors.amount?.message.toString()}
                  >
                    <Label>Amount</Label>
                    <AmountInput
                      placeholder="0.00"
                      controllerField={amountField}
                    />
                    <FieldStatusMessage />
                  </Field>

                  {type !== 'transfer' && (
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Checkbox
                          id="refund"
                          ref={refundField.ref}
                          checked={refundField.value}
                          onCheckedChange={handleRefundChange}
                        />
                        <Label htmlFor="refund" className="font-normal">
                          Refund
                        </Label>
                      </div>
                      {!isPastOrTodayEdit && !(behaviourAutoReset && !isSeriesPastEdit) && (
                        <div className="flex items-center gap-1">
                          <Label htmlFor="estimateOnly" className="font-normal">
                            Estimate Only
                          </Label>
                          <Checkbox
                            id="estimateOnly"
                            checked={behaviourField.value === 'EstimateOnly'}
                            onCheckedChange={(checked) =>
                              behaviourField.onChange(checked ? 'EstimateOnly' : 'Standard')
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Field
                    status={errors.description?.message ? 'error' : undefined}
                    statusText={errors.description?.message.toString()}
                  >
                    <Label>Description</Label>
                    <Textarea
                      {...register('description', { shouldUnregister: true })}
                    />
                    <FieldStatusMessage />
                  </Field>
                </div>
                {type === 'in' &&
                  !refundField.value &&
                  Number.isFinite(
                    parseFloat(unformatNumeral(amountField.value ?? '')),
                  ) &&
                  parseFloat(unformatNumeral(amountField.value ?? '')) !== 0 &&
                  (moneyPaths?.length ?? 0) > 0 && (
                    <div>
                      <hr />
                      <p>Money Paths</p>
                      <div className="mb-2 mt-2 flex items-center gap-1">
                        <Checkbox
                          id="isSkipAllocations"
                          ref={isSkipAllocationsField.ref}
                          checked={isSkipAllocationsField.value}
                          onCheckedChange={handleIsSkipAllocationsChange}
                        />
                        <Label
                          htmlFor="isSkipAllocations"
                          className="font-normal"
                        >
                          Skip Allocations
                        </Label>
                      </div>
                      {!isSkipAllocationsField.value && renderAllocations()}
                    </div>
                  )}
              </div>
            </TabsContent>
          </Tabs>
        </NonModalWindow.Body>
        <NonModalWindow.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setShowConfirmationModal(isDirty)

              if (!isDirty) {
                if (location.pathname.includes('transaction-list')) {
                  navigateTo('list', true)
                  // navigate(-1)
                } else {
                  closeToOrigin()
                }
              }
            }}
          >
            Cancel
          </Button>
          {isPastOrTodayEdit && editTransaction?.can_actualise && (
            <Button
              type="button"
              variant="secondary"
              loading={actualiseStatus.isPending}
              onClick={async () => {
                await actualise({})
                handleClose()
              }}
            >
              Actualise
            </Button>
          )}
          <Button
            type="submit"
            loading={
              createTransactionStatus.isPending ||
              updateTransactionStatus.isPending ||
              createTransactionSeriesStatus.isPending ||
              updateTransactionSeriesStatus.isPending ||
              updateTransferStatus.isPending
            }
          >
            Save
          </Button>
        </NonModalWindow.Footer>
      </NonModalWindow>
    </>
  )
}
